// file: src/components/modals/UploadFilesModal.jsx
import React, { useState, useEffect } from 'react';
import { Upload, FileText, AlertTriangle, CheckCircle, Loader, Server, SquareTerminal, Cpu, Layers2, Box, X } from 'lucide-react';
import { getPresignedUrl, uploadToPresignedUrl, confirmUpload, runDocs } from '../../services/docServices';
import FileTypeIcon from '../icons/FileTypeIcon';
import { detectFileType } from '../../utils/fileUtils';
import BlenderServerManager from './BlenderServerManager';
import FreeCADServerManager from './FreeCADServerManager';
import JupyterLabServerManager from './JupyterLabServerManager';
import KiCadServerManager from './KiCadServerManager';
import { useTheme } from '../../context/ThemeContext';
import styles from './ConnectDataSourcesModal.module.css';

function ConnectDataSourcesModal({ onClose, onFilesUploaded, getKbId, kbId: initialKbId = null }) {
  const { theme } = useTheme();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadError, setUploadError] = useState('');
  const [kbId, setKbId] = useState(initialKbId);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload', 'blender', 'freecad', 'jupyterlab', or 'kicad'

  // Fetch the kbId if it's not provided initially
  useEffect(() => {
    async function fetchKbId() {
      if (!initialKbId && getKbId) {
        try {
          const id = await getKbId();
          setKbId(id);
        } catch (err) {
          console.error('Error getting KB ID:', err);
          setUploadError('Failed to get knowledge base. Please try again.');
        }
      } else if (initialKbId) {
        setKbId(initialKbId);
      }
    }
    fetchKbId();
  }, [initialKbId, getKbId]);

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files || []));
    setUploadProgress({});
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) {
      setUploadError('No files selected!');
      return;
    }
    
    // If kbId is not yet available, try to get it again
    let currentKbId = kbId;
    
    if (!currentKbId && getKbId) {
      try {
        currentKbId = await getKbId();
        setKbId(currentKbId);
      } catch (err) {
        console.error('Error getting KB ID:', err);
        setUploadError('Failed to get knowledge base. Please try again.');
        return;
      }
    }
    
    if (!currentKbId) {
      setUploadError('No knowledge base ID found for this phase. Please try refreshing the page or contact support.');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const uploadedDocs = [];
      
      // Process each file individually
      for (const file of selectedFiles) {
        try {
          // Step 1: Get a pre-signed URL from the backend
          const presignedData = await getPresignedUrl(currentKbId, file);
          
          // Better error checking for the presigned URL data
          if (!presignedData) {
            throw new Error('Failed to get pre-signed URL response');
          }
          
          const { presigned_url, doc_id, file_location, bucket } = presignedData;
          
          if (!presigned_url) {
            throw new Error('Pre-signed URL is missing in the response');
          }
          
          // Step 2: Upload the file directly to Storage using the pre-signed URL
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'uploading', progress: 0 }
          }));
          
          await uploadToPresignedUrl(presigned_url, file);
          
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'confirming', progress: 90 }
          }));
          
          // Step 3: Confirm the upload to the backend
          const docInfo = await confirmUpload(doc_id, bucket, file_location);
          
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'complete', progress: 100 }
          }));
          
          uploadedDocs.push(docInfo);
        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'error', error: fileError.message }
          }));
        }
      }

      // Queue parsing for successfully uploaded docs
      if (uploadedDocs.length > 0) {
        const docIds = uploadedDocs.map(doc => doc.id);
        
        runDocs(docIds)
          .then(() => {
            // After successfully queueing the docs, update their run status in the local state
            // This ensures all docs show as "processing" in the UI immediately
            if (onFilesUploaded) {
              // Include run=1 information with the uploaded docs
              uploadedDocs.forEach(doc => {
                doc.run = '1';  // Set run=1 directly in the doc objects
                doc.progress = 0;
                doc.progress_msg = 'Starting processing...';
              });
              
              // Call onFilesUploaded with the updated docs to trigger parent component update
              onFilesUploaded(uploadedDocs);
            }
          })
          .catch(runErr => {
            console.error('Failed to queue document processing:', runErr);
            // Even on error, still notify parent about uploaded docs
            if (onFilesUploaded) {
              onFilesUploaded(uploadedDocs);
            }
          });
      }

      // Only close the modal if all files were uploaded successfully
      if (uploadedDocs.length === selectedFiles.length) {
        // Notify parent and close the modal
        onFilesUploaded?.(uploadedDocs);
        onClose();
      } else if (uploadedDocs.length > 0) {
        // Some files were uploaded, notify parent but keep modal open to show errors
        onFilesUploaded?.(uploadedDocs);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(`Failed to upload: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Helper to render progress bar
  const renderProgress = (fileName) => {
    const fileProgress = uploadProgress[fileName];
    if (!fileProgress) return null;
    
    if (fileProgress.status === 'error') {
      return (
        <div className="flex items-center text-sm text-red-500 mt-1">
          <AlertTriangle className="h-4 w-4 mr-1" />
          {fileProgress.error}
        </div>
      );
    }
    
    return (
      <div className="mt-2">
        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-muted-foreground mb-1">
          <span>
            {fileProgress.status === 'uploading' ? 'Uploading...' :
             fileProgress.status === 'confirming' ? 'Processing...' :
             'Complete'}
          </span>
          <span>{fileProgress.progress}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="h-2 rounded-full transition-all duration-200" 
            style={{ 
              width: `${fileProgress.progress}%`,
              background: 'linear-gradient(135deg, #00ffff, #0080ff)'
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modalContainer}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <Upload className="h-4 w-4" />
            </div>
            <h2 className={styles.title}>Connect data sources</h2>
            <button
              onClick={onClose}
              className={styles.closeButton}
              type="button"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Tab Navigation */}
          <div className={styles.tabNavigation}>
            <button
              onClick={() => setActiveTab('upload')}
              className={`${styles.tab} ${activeTab === 'upload' ? styles.tabActive : ''}`}
            >
              <FileText className="h-4 w-4 mr-2" />
              Files
            </button>
            <button
              onClick={() => setActiveTab('blender')}
              className={`${styles.tab} ${activeTab === 'blender' ? styles.tabActive : ''}`}
            >
              <Layers2 className="h-4 w-4 mr-2" />
              Blender
            </button>
            <button
              onClick={() => setActiveTab('freecad')}
              className={`${styles.tab} ${activeTab === 'freecad' ? styles.tabActive : ''}`}
            >
              <Box className="h-4 w-4 mr-2" />
              FreeCAD
            </button>
            <button
              onClick={() => setActiveTab('jupyterlab')}
              className={`${styles.tab} ${activeTab === 'jupyterlab' ? styles.tabActive : ''}`}
            >
              <SquareTerminal className="h-4 w-4 mr-2" />
              JupyterLab
            </button>
            <button
              onClick={() => setActiveTab('kicad')}
              className={`${styles.tab} ${activeTab === 'kicad' ? styles.tabActive : ''}`}
            >
              <Cpu className="h-4 w-4 mr-2" />
              KiCad
            </button>
          </div>
          {activeTab === 'upload' && (
            <p className={styles.description}>
              Select one or more files. Max file size is 20MB each.
            </p>
          )}
          {activeTab === 'blender' && (
            <p className={styles.description}>
              Create, start, stop, share or delete your Blender servers.
            </p>
          )}
          {activeTab === 'freecad' && (
            <p className={styles.description}>
              Create, start, stop, share or delete your FreeCAD servers.
            </p>
          )}
          {activeTab === 'jupyterlab' && (
            <p className={styles.description}>
              Create, start, stop, share or delete your JupyterLab servers.
            </p>
          )}
          {activeTab === 'kicad' && (
            <p className={styles.description}>
              Create, start, stop, share or delete your KiCad servers.
            </p>
          )}
        </div>
        {uploadError && activeTab === 'upload' && (
          <div className="flex items-center p-3 rounded-md bg-red-500/10 border border-red-500/20 mb-4">
            <AlertTriangle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-500">{uploadError}</p>
          </div>
        )}
        <div className="space-y-4 flex-1 min-h-0">
          {activeTab === 'upload' && (
            <>
          {/* File Input Section */}
          <div className={styles.uploadSection}>
            <label htmlFor="fileInput" className={styles.fileInputLabel}>
              <div className={styles.uploadArea}>
                <div className={styles.uploadAreaContent}>
                  <div className={styles.uploadIcon}>
                    <FileText />
                  </div>
                  <p className={styles.uploadText}>
                    Click to select files
                  </p>
                  <p className={styles.uploadSubtext}>
                    PDF, DOC, DOCX, CSV
                  </p>
                </div>
              </div>
            </label>
            <input
              id="fileInput"
              type="file"
              multiple
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.docx,.csv"
              disabled={isUploading}
            />
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              <h3 className="text-sm font-medium text-foreground">Selected Files ({selectedFiles.length})</h3>
              <div className="space-y-2">
                {selectedFiles.map((file) => (
                  <div key={file.name} className="flex items-start space-x-3 p-3 rounded-lg border border-input bg-background/50">
                    <div className="flex-shrink-0 mt-0.5">
                      <FileTypeIcon type={detectFileType(file.name)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-muted-foreground">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                      {renderProgress(file.name)}
                    </div>
                    {uploadProgress[file.name]?.status === 'complete' && (
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    )}
                    {uploadProgress[file.name]?.status === 'error' && (
                      <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            </div>
              )}
            </>
          )}

          {activeTab === 'blender' && (
            <BlenderServerManager 
              tenantId={kbId} 
              kbId={kbId}
              onError={(error) => setUploadError(error)}
              onServerConnected={(result) => {
                // Notify parent about the connected server
                if (onFilesUploaded) {
                  // Create a virtual file object that represents the Blender server
                  const virtualFile = {
                    id: result.doc_id,
                    name: result.virtual_doc_name,
                    type: 'blender', // <-- ensure this is 'blender'
                    url: result.location || `/v1/eks_blender/proxy/${result.server_name}`,
                    server_name: result.server_name,
                    kb_id: result.kb_id,
                    status: '1', // Active status
                    progress: 1, // Already "processed" since it's a virtual file
                    run: '0' // Not running processing since it's a virtual file
                  };
                  onFilesUploaded([virtualFile]);
                }
                // Close the modal after successful connection
                onClose();
              }}
            />
          )}

          {activeTab === 'freecad' && (
            <FreeCADServerManager 
              tenantId={kbId} 
              kbId={kbId}
              onError={(error) => setUploadError(error)}
              onServerConnected={(result) => {
                // Notify parent about the connected server
                if (onFilesUploaded) {
                  // Create a virtual file object that represents the FreeCAD server
                  const virtualFile = {
                    id: result.doc_id,
                    name: result.virtual_doc_name,
                    type: 'freecad', // <-- ensure this is 'freecad'
                    url: result.location || `/v1/eks_freecad/proxy/${result.server_name}`,
                    server_name: result.server_name,
                    kb_id: result.kb_id,
                    status: '1', // Active status
                    progress: 1, // Already "processed" since it's a virtual file
                    run: '0' // Not running processing since it's a virtual file
                  };
                  onFilesUploaded([virtualFile]);
                }
                // Close the modal after successful connection
                onClose();
              }}
            />
          )}

          {activeTab === 'jupyterlab' && (
            <JupyterLabServerManager 
              tenantId={kbId} 
              kbId={kbId}
              onError={(error) => setUploadError(error)}
              onServerConnected={(result) => {
                // Notify parent about the connected server
                if (onFilesUploaded) {
                  // Create a virtual file object that represents the JupyterLab server
                  const virtualFile = {
                    id: result.doc_id,
                    name: result.virtual_doc_name,
                    type: 'jupyterlab', // <-- ensure this is 'jupyterlab'
                    url: result.location || `/v1/eks_jupyterlab/proxy/${result.server_name}`,
                    server_name: result.server_name,
                    kb_id: result.kb_id,
                    status: '1', // Active status
                    progress: 1, // Already "processed" since it's a virtual file
                    run: '0' // Not running processing since it's a virtual file
                  };
                  onFilesUploaded([virtualFile]);
                }
                // Close the modal after successful connection
                onClose();
              }}
            />
          )}

          {activeTab === 'kicad' && (
            <KiCadServerManager 
              tenantId={kbId} 
              kbId={kbId}
              onError={(error) => setUploadError(error)}
              onServerConnected={(result) => {
                // Notify parent about the connected server
                if (onFilesUploaded) {
                  // Create a virtual file object that represents the KiCad server
                  const virtualFile = {
                    id: result.doc_id,
                    name: result.virtual_doc_name,
                    type: 'kicad', // <-- ensure this is 'kicad'
                    url: result.location || `/v1/eks_kicad/proxy/${result.server_name}`,
                    server_name: result.server_name,
                    kb_id: result.kb_id,
                    status: '1', // Active status
                    progress: 1, // Already "processed" since it's a virtual file
                    run: '0' // Not running processing since it's a virtual file
                  };
                  onFilesUploaded([virtualFile]);
                }
                // Close the modal after successful connection
                onClose();
              }}
            />
          )}
        </div>

        {activeTab === 'upload' && (
        <div className={styles.footer}>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={isUploading || selectedFiles.length === 0}
            className={styles.uploadButton}
          >
            {isUploading ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Upload Files
              </>
            )}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
export default ConnectDataSourcesModal;


